import {
  createTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
} from '@tanstack/table-core';
import type { SortingState, PaginationState } from '@tanstack/table-core';
import {
  type DatatableDefinition,
  type ServerDatatableDefinition,
  type DatatableRow,
  type DatatableLocalization,
  type DatatableResponse,
  type DatatableActionDefinition,
  SortOrder,
} from '../../../types/datatable-types';
import {
  createTanStackColumns,
  createSortingState,
  createPaginationParams,
  parseBackendResponse,
} from './adapters';
import { renderActionColumn } from './action-renderer';
import { initTableActions } from './table-actions';
import { initFetchModal } from '../modal-fetch';
import { initConfirmModal } from '../modal-confirm';
import { executePrimaryAction } from './row-action-handler';

/**
 * Type guard to detect server-side datatable definition
 * @param definition - Datatable definition to check
 * @returns True if definition is ServerDatatableDefinition
 */
function isServerSide(definition: DatatableDefinition): definition is ServerDatatableDefinition {
  return 'Url' in definition;
}

/**
 * Simple reactive signal implementation
 */
function createSignal<T>(initialValue: T): [() => T, (value: T | ((prev: T) => T)) => void] {
  let value = initialValue;
  return [
    () => value,
    (newValue) => {
      value = typeof newValue === 'function' ? (newValue as (prev: T) => T)(value) : newValue;
    },
  ];
}

/**
 * Serializes non-empty form values to query params.
 * Repeated keys are preserved for multi-select fields.
 * @returns URLSearchParams with form data
 */
function serializeForm(form: HTMLFormElement): URLSearchParams {
  const result = new URLSearchParams();
  const formData = new FormData(form);

  formData.forEach((value, key) => {
    const valueStr = String(value ?? '').trim();
    if (valueStr !== '') {
      result.append(key, valueStr);
    }
  });

  return result;
}

/**
 * Initialize all datatable instances in the container
 * @param container - Container element (defaults to document)
 */
export async function initDatatable(container: HTMLElement | Document = document): Promise<void> {
  const datatables = container.querySelectorAll<HTMLElement>(
    '.datatable:not([data-datatable-initialized])'
  );

  for (const datatableContainer of datatables) {
    try {
      // Parse definition
      const definitionAttr = datatableContainer.getAttribute('data-datatable-definition');
      if (!definitionAttr) {
        // eslint-disable-next-line no-console
        console.warn('Datatable missing data-datatable-definition attribute');
        continue;
      }

      const definition: DatatableDefinition = JSON.parse(definitionAttr);

      // Find table element
      const tableElement = datatableContainer.querySelector<HTMLTableElement>('.datatable__table');
      if (!tableElement) {
        // eslint-disable-next-line no-console
        console.warn('Datatable missing .datatable__table element');
        continue;
      }

      // Parse localization
      const localizationAttr = datatableContainer.getAttribute('data-localization');
      const localization: DatatableLocalization = localizationAttr
        ? JSON.parse(localizationAttr)
        : { Loading: 'Loading...', EmptyListMessage: 'No records found' };

      // Create instance
      await createDatatableInstance(datatableContainer, tableElement, definition, localization);

      // Mark as initialized
      datatableContainer.setAttribute('data-datatable-initialized', 'true');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error initializing datatable:', error);
    }
  }
}

/**
 * Creates a single datatable instance
 * @param container - .datatable container element
 * @param tableElement - table.datatable__table element
 * @param definition - Parsed datatable definition
 * @param localization - Localization strings
 */
async function createDatatableInstance(
  container: HTMLElement,
  tableElement: HTMLTableElement,
  definition: DatatableDefinition,
  localization: DatatableLocalization
): Promise<void> {
  const isServerMode = isServerSide(definition);

  // Setup state
  const [sorting, setSorting] = createSignal<SortingState>(
    isServerMode && definition.Sorts
      ? definition.Sorts.map((s) => ({
          id: s.FieldName,
          desc: s.SortOrder === SortOrder.Descending,
        }))
      : []
  );
  const [pagination, setPagination] = createSignal<PaginationState>({
    pageIndex: isServerMode ? (definition.DefaultPageNumber ?? 0) : 0,
    pageSize: isServerMode ? definition.DefaultPageSize : 25,
  });
  const [data, setData] = createSignal<DatatableRow[]>(
    isServerMode ? (definition.InitialData ?? []) : definition.Data
  );
  const [totalCount, setTotalCount] = createSignal<number>(
    isServerMode ? 0 : definition.Data.length
  );
  const [loading, setLoading] = createSignal<boolean>(false);

  // Data fetching function (server-side only)
  async function fetchData(): Promise<void> {
    if (!isServerMode) {
      // eslint-disable-next-line no-console
      console.warn('fetchData called on client-side datatable');
      return;
    }

    setLoading(true);
    renderTable();

    try {
      // Build request
      const paginationParams = createPaginationParams(pagination());
      const sortParams = createSortingState(sorting());

      // Serialize filter form if exists
      const filterForm = container.querySelector(
        '.datatable__filter-form form'
      ) as HTMLFormElement | null;
      const filterData = filterForm ? serializeForm(filterForm) : new URLSearchParams();

      // Merge all parameters
      const params = new URLSearchParams();
      params.set('PageSize', String(paginationParams.PageSize));
      params.set('PageNumber', String(paginationParams.PageNumber));
      filterData.forEach((value, key) => {
        params.append(key, value);
      });

      // Add sorts
      sortParams.forEach((sort, index) => {
        params.set(`Sorts[${index}].FieldName`, sort.FieldName);
        params.set(`Sorts[${index}].SortOrder`, String(sort.SortOrder));
      });

      // Fetch from server
      const separator = definition.Url.includes('?') ? '&' : '?';
      const url = `${definition.Url}${separator}${params.toString()}`;
      const response = await fetch(url);
      const json: DatatableResponse<DatatableRow> = await response.json();

      // Parse response
      const { data: newData, totalCount: count, queryString } = parseBackendResponse(json);

      setData(newData);
      setTotalCount(count);

      // Update browser URL if queryString provided
      if (queryString !== undefined) {
        const newUrl = `${window.location.pathname}${queryString}`;
        window.history.pushState({}, '', newUrl);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching datatable data:', error);
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      renderTable();
    }
  }

  // Build a lookup map for column definitions by FieldName (used during TD rendering)
  const columnDefMap = new Map<string, (typeof definition.Columns)[number]>(
    definition.Columns.map((c) => [c.FieldName, c])
  );

  // Create TanStack table
  const columns = createTanStackColumns(definition.Columns, definition.HasActionsColumn, (row) =>
    renderActionColumn(row)
  );

  const tableConfig = {
    data: data(),
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting: sorting(),
      pagination: pagination(),
    },
    onStateChange: () => {
      // State change handler (required by TanStack)
    },
    renderFallbackValue: null,
    ...(isServerMode
      ? {
          // Server-side: manual sorting and pagination
          manualSorting: true,
          manualPagination: true,
          pageCount: Math.ceil(totalCount() / pagination().pageSize),
          onSortingChange: (updater) => {
            const newSorting = typeof updater === 'function' ? updater(sorting()) : updater;
            setSorting(newSorting);
            fetchData();
          },
          onPaginationChange: (updater) => {
            const newPagination = typeof updater === 'function' ? updater(pagination()) : updater;
            setPagination(newPagination);
            fetchData();
          },
        }
      : {
          // Client-side: automatic sorting and pagination
          manualSorting: false,
          manualPagination: false,
          getSortedRowModel: getSortedRowModel(),
          getPaginationRowModel: getPaginationRowModel(),
          onSortingChange: (updater) => {
            const newSorting = typeof updater === 'function' ? updater(sorting()) : updater;
            setSorting(newSorting);
            renderTable();
          },
          onPaginationChange: (updater) => {
            const newPagination = typeof updater === 'function' ? updater(pagination()) : updater;
            setPagination(newPagination);
            renderTable();
          },
        }),
  };

  const table = createTable<DatatableRow>(tableConfig);

  // Render table function
  function renderTable(): void {
    const tbody = tableElement.querySelector('tbody');
    if (!tbody) return;

    // Handle loading state UI
    if (loading()) {
      container.classList.add('is-loading');

      return;
    } else {
      container.classList.remove('is-loading');
    }

    // Clear existing rows
    tbody.innerHTML = '';

    if (data() === undefined || data().length === 0) {
      const tr = document.createElement('tr');
      const td = document.createElement('td');
      td.setAttribute('colspan', '100');
      td.className = 'text-center';
      td.textContent = localization.EmptyListMessage;
      tr.appendChild(td);
      tbody.appendChild(tr);
      return;
    }

    // Update table options with new data
    table.options.data = data();
    table.options.pageCount = Math.ceil(totalCount() / pagination().pageSize);
    table.options.state = {
      sorting: sorting(),
      pagination: pagination(),
    };

    // Render rows
    table.getRowModel().rows.forEach((row) => {
      const tr = document.createElement('tr');

      // Store row data for click handling
      const rowId = String(row.original[definition.RowIdFieldName]);
      tr.dataset.rowId = rowId;

      // Check if row has a primary action
      const actions = row.original.Actions as DatatableActionDefinition[];
      const primaryAction = actions?.find((a) => a.IsPrimary);

      if (primaryAction) {
        tr.classList.add('has-primary-action');
        tr.dataset.primaryAction = JSON.stringify(primaryAction);
      }

      // Checkbox column if mass actions exist
      if (definition.TableActions.some((a) => a.MassActionSetting.IsMassAction)) {
        const checkboxTd = document.createElement('td');
        checkboxTd.className = 'checks-col checks-col-clickable';
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'form-check-input row-checkbox';
        checkbox.dataset.rowId = rowId;
        checkboxTd.appendChild(checkbox);

        // Make entire cell clickable for checkbox toggle
        checkboxTd.addEventListener('click', (e) => {
          // Only handle clicks on the cell itself, not the checkbox
          if (e.target === checkboxTd) {
            checkbox.checked = !checkbox.checked;
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        tr.appendChild(checkboxTd);
      }

      // Data columns
      row.getAllCells().forEach((cell) => {
        const td = document.createElement('td');

        // Special handling for actions column
        if (cell.column.id === 'actions') {
          td.className = 'actions-col';
          // Call the cell renderer function to get HTMLElement
          const renderer = cell.column.columnDef.cell;
          if (renderer && typeof renderer === 'function') {
            const element = renderer(cell.getContext());
            if (element instanceof HTMLElement) {
              td.appendChild(element);
            }
          }
        } else {
          const colDef = columnDefMap.get(cell.column.id);

          // Apply column constraints
          if (colDef) {
            if (!colDef.WrapAllowed) {
              td.classList.add('col-nowrap');
            }
            if (colDef.MinWidth !== undefined && colDef.MinWidth !== null) {
              td.style.minWidth = `${colDef.MinWidth}px`;
            }
            if (colDef.Center) {
              td.classList.add('text-center');
            }
          }

          const value = cell.getValue();
          const valueStr = String(value ?? '');

          // Check if value contains HTML tags
          if (valueStr.match(/<[^>]+>/)) {
            // Render as HTML
            td.innerHTML = valueStr;
          } else {
            // Render as text (safe from XSS)
            td.textContent = valueStr;
          }
        }

        tr.appendChild(td);
      });

      tbody.appendChild(tr);
    });

    // Add row click handler for primary actions
    tbody.querySelectorAll<HTMLElement>('tr.has-primary-action').forEach((tr) => {
      tr.addEventListener('click', async (e) => {
        const target = e.target as HTMLElement;

        // Exception 1: Ignore clicks on checkbox column
        if (target.closest('.checks-col')) {
          return;
        }

        // Exception 2: Ignore clicks on action column
        if (target.closest('.actions-col')) {
          return;
        }

        // Exception 3: Ignore clicks on links
        if (target.closest('a') || target.tagName === 'A') {
          return;
        }

        // Exception 4: Ignore clicks on buttons
        if (target.closest('button') || target.tagName === 'BUTTON') {
          return;
        }

        // Execute primary action
        const primaryActionJson = tr.dataset.primaryAction;
        if (primaryActionJson) {
          try {
            const primaryAction = JSON.parse(primaryActionJson) as DatatableActionDefinition;
            await executePrimaryAction(primaryAction);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error executing primary action:', error);
          }
        }
      });
    });

    // Re-initialize modals and confirms for action buttons
    initFetchModal(tbody);
    initConfirmModal(tbody);

    renderPaginationControls();
    renderSortingIndicators();
    setupScrollShadow();
  }

  // Render pagination controls
  function renderPaginationControls(): void {
    let paginationContainer = container.querySelector<HTMLElement>('.datatable__pagination');
    if (!paginationContainer) {
      paginationContainer = document.createElement('div');
      paginationContainer.className = 'datatable__pagination';
      const cardBody = container.querySelector('.card-body');
      if (cardBody) {
        cardBody.appendChild(paginationContainer);
      }
    }

    const currentPage = pagination().pageIndex;
    const pageCount = table.getPageCount();
    const pageSizes = isServerMode ? definition.PageSizes : [10, 25, 50, 100];

    const visiblePageCount = Math.min(3, pageCount);
    let pageStart = Math.max(0, currentPage - 1);
    let pageEnd = Math.min(pageCount - 1, currentPage + 1);

    while (pageEnd - pageStart + 1 < visiblePageCount) {
      if (pageStart > 0) {
        pageStart--;
        continue;
      }
      if (pageEnd < pageCount - 1) {
        pageEnd++;
        continue;
      }
      break;
    }

    const visiblePages: number[] = [];
    for (let page = pageStart; page <= pageEnd; page++) {
      visiblePages.push(page);
    }

    paginationContainer.innerHTML = `
      <div class="datatable__pagination-wrapper">
        <div class="datatable__page-size-selector">
          <label class="form-label" for="datatable-page-size-select">${localization.PageSizeLabel ?? 'Rows per page'}</label>
          <select class="form-select form-select-sm datatable__page-size" id="datatable-page-size-select" aria-label="${localization.PageSizeLabel ?? 'Rows per page'}">
            ${Array.from(pageSizes)
              .map(
                (size) =>
                  `<option value="${size}" ${size === pagination().pageSize ? 'selected' : ''}>${size}</option>`
              )
              .join('')}
          </select>
        </div>
        <nav aria-label="Table pagination">
          <ul class="pagination pagination-sm mb-0">
            <li class="page-item ${currentPage === 0 ? 'disabled' : ''}">
              <a class="page-link" href="#" data-action="first" title="${localization.First ?? 'First'}">&laquo;</a>
            </li>
            <li class="page-item ${currentPage === 0 ? 'disabled' : ''}">
              <a class="page-link" href="#" data-action="prev" title="${localization.Previous ?? 'Previous'}">&lsaquo;</a>
            </li>
            ${visiblePages
              .map(
                (page) => `
            <li class="page-item ${page === currentPage ? 'active' : ''}">
              ${
                page === currentPage
                  ? `<span class="page-link">${page + 1}</span>`
                  : `<a class="page-link" href="#" data-page-index="${page}" title="Page ${page + 1}">${page + 1}</a>`
              }
            </li>`
              )
              .join('')}
            <li class="page-item ${currentPage >= pageCount - 1 ? 'disabled' : ''}">
              <a class="page-link" href="#" data-action="next" title="${localization.Next ?? 'Next'}">&rsaquo;</a>
            </li>
            <li class="page-item ${currentPage >= pageCount - 1 ? 'disabled' : ''}">
              <a class="page-link" href="#" data-action="last" title="${localization.Last ?? 'Last'}">&raquo;</a>
            </li>
          </ul>
        </nav>
      </div>
    `;

    // Bind pagination button handlers
    paginationContainer.querySelectorAll('[data-action]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const action = (e.currentTarget as HTMLElement).dataset.action;
        switch (action) {
          case 'first':
            table.setPageIndex(0);
            break;
          case 'prev':
            table.previousPage();
            break;
          case 'next':
            table.nextPage();
            break;
          case 'last':
            table.setPageIndex(pageCount - 1);
            break;
        }
      });
    });

    // Bind direct page number handlers
    paginationContainer.querySelectorAll('[data-page-index]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const pageIndex = parseInt((e.currentTarget as HTMLElement).dataset.pageIndex ?? '', 10);
        if (!Number.isNaN(pageIndex)) {
          table.setPageIndex(pageIndex);
        }
      });
    });

    // Bind page size selector
    paginationContainer.querySelector('.datatable__page-size')?.addEventListener('change', (e) => {
      const newSize = parseInt((e.target as HTMLSelectElement).value);
      // Update pagination via table API to trigger onPaginationChange
      table.setPagination({ ...pagination(), pageSize: newSize, pageIndex: 0 });
    });
  }

  // Render sorting indicators
  function renderSortingIndicators(): void {
    const headers = tableElement.querySelectorAll('th');
    const hasMassActions = definition.TableActions.some((a) => a.MassActionSetting.IsMassAction);

    headers.forEach((th, index) => {
      // Skip checkbox and actions columns
      if (th.classList.contains('checks-col') || th.classList.contains('actions-col')) {
        return;
      }

      // Calculate column index accounting for checkbox column
      const columnIndex = hasMassActions ? index - 1 : index;
      const column = table.getAllColumns()[columnIndex];

      if (!column || !column.getCanSort()) {
        return;
      }

      th.classList.add('col-sortable');

      // Remove existing sort classes
      th.classList.remove('sort-asc', 'sort-desc', 'sort-none');

      const sortState = sorting().find((s) => s.id === column.id);
      if (sortState) {
        th.classList.add(sortState.desc ? 'sort-desc' : 'sort-asc');
        th.title = sortState.desc
          ? (localization.SortAscending ?? 'Sort ascending')
          : (localization.SortDescending ?? 'Sort descending');
      } else {
        th.classList.add('sort-none');
        th.title = localization.SortAscending ?? 'Sort ascending';
      }

      // Bind click handler for sorting
      th.style.cursor = 'pointer';
      th.onclick = () => {
        const currentSort = sorting().find((s) => s.id === column.id);
        const nextDesc = currentSort ? !currentSort.desc : false;
        // Toggle sorting using API, ensuring single column sort (multi=false)
        column.toggleSorting(nextDesc, false);
      };
    });
  }

  // Setup filter form integration
  const filterForms = container.getElementsByClassName('datatable__filter-form');
  if (filterForms.length > 0) {
    const filterForm = filterForms[0].querySelector('form');
    if (filterForm) {
      filterForm.addEventListener('submit', (e) => {
        e.preventDefault();
        setPagination({ ...pagination(), pageIndex: 0 });
        fetchData();
      });

      // Debounced filter refresh
      let debounceTimer: number | undefined;
      const debouncedFetch = () => {
        clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(() => {
          setPagination({ ...pagination(), pageIndex: 0 });
          fetchData();
        }, 300);
      };

      // Trigger on input for text fields (debounced)
      filterForm
        .querySelectorAll('input[type="text"], input[type="search"], textarea')
        .forEach((input) => {
          input.addEventListener('input', debouncedFetch);
        });

      // Trigger immediately on change for selects and other inputs
      filterForm
        .querySelectorAll('select, input:not([type="text"]):not([type="search"])')
        .forEach((input) => {
          input.addEventListener('change', () => {
            setPagination({ ...pagination(), pageIndex: 0 });
            fetchData();
          });
        });
    }
  }

  // Initialize table actions
  if (definition.TableActions.length > 0) {
    initTableActions(container, definition, localization, () => {
      const checkboxes = container.querySelectorAll<HTMLInputElement>('.row-checkbox:checked');
      return Array.from(checkboxes).map((cb) => cb.dataset.rowId || '');
    });
  }

  // Setup scroll shadow for action column
  function setupScrollShadow(): void {
    const tableResponsive = container.querySelector('.table-responsive');
    if (!tableResponsive || !definition.HasActionsColumn) return;

    const updateShadow = () => {
      const scrollLeft = tableResponsive.scrollLeft;
      const scrollWidth = tableResponsive.scrollWidth;
      const clientWidth = tableResponsive.clientWidth;
      const isScrollable = scrollWidth > clientWidth;
      const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 2;

      const actionsCol = container.querySelectorAll('.actions-col');
      actionsCol.forEach((col) => {
        if (isScrollable && !isAtEnd) {
          col.classList.add('shadow-on-scroll');
        } else {
          col.classList.remove('shadow-on-scroll');
        }
      });
    };

    // Remove existing listener if any
    tableResponsive.removeEventListener('scroll', updateShadow);
    tableResponsive.addEventListener('scroll', updateShadow);

    // Initial check after render
    requestAnimationFrame(() => {
      updateShadow();
    });
  }

  // Initial data load
  if (isServerMode) {
    // Server-side: fetch data from API
    await fetchData();
  } else {
    // Client-side: render with provided data
    renderTable();
  }
}
